<script setup lang="ts">
import { computed, inject, useAttrs, useId, type SelectHTMLAttributes, type StyleValue } from 'vue';
import { ChevronDownIcon } from '@blpde/nx-icons';
import { labelInjectionKey } from './internal';
import { useInputSaveStatus } from './composables/useInputSaveStatus';
import NxSaveStateBadge from './NxSaveStateBadge.vue';

export type NxNativeSelectOption<T extends string | number = string | number> = {
  value: T;
  label: string;
  disabled?: boolean;
};

/**
 * `ribbon` is the office ribbon's control: the ribbon's height, type and
 * border (`--office-ribbon-*`, set by NxOfficeRibbon), the surface fill, and a
 * width the consumer sets on the component (its root) — font family, font size
 * and a paste mode all sit side by side in one ribbon row.
 */
export type NxNativeSelectSize = 'compact' | 'field' | 'full' | 'ribbon';

const props = withDefaults(
  defineProps<
    {
      options: NxNativeSelectOption<string | number>[];
      placeholder?: string;
      disabled?: boolean;
      size?: NxNativeSelectSize;
      id?: string;
    } & /* @vue-ignore */ Omit<SelectHTMLAttributes, 'value' | 'disabled' | 'size'>
  >(),
  {
    size: 'field',
    placeholder: undefined,
    disabled: false,
    id: undefined,
  }
);

const model = defineModel<string | number | null>({ required: true });

// Fallthrough attributes are split: `class`/`style` stay on the root span
// (consumers use them for layout, e.g. flex sizing), everything else —
// aria-*, name, autocomplete, title, data-*, required, event listeners —
// lands on the native <select> so it can be labelled and addressed directly.
defineOptions({ inheritAttrs: false });
const attrs = useAttrs();
const rootAttrs = computed(() => ({ class: attrs.class, style: attrs.style as StyleValue }));
const selectAttrs = computed(() =>
  Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== 'class' && key !== 'style'))
);

let elementId = props.id;
const labelInjection = inject(labelInjectionKey, null);
if (labelInjection && !labelInjection.for.value) {
  elementId ??= useId();
  labelInjection.setFor(elementId);
}

const selectValue = computed({
  get: () => (model.value === null ? '' : String(model.value)),
  set: (raw: string) => {
    if (raw === '') {
      model.value = null;
      return;
    }
    const match = props.options.find((opt) => String(opt.value) === raw);
    model.value = match?.value ?? raw;
  },
});

// Save status (loading/success/error) from an ancestor NxLoadingWrapper.
const {
  visualState,
  errorMessage,
  fadeOut,
  srText,
  ariaBindings,
  disabled: statusDisabled,
} = useInputSaveStatus();
const isDisabled = computed(() => props.disabled || statusDisabled.value);
</script>

<template>
  <span
    class="nx-native-select-root"
    :class="{ 'root-full': props.size === 'full', 'root-ribbon': props.size === 'ribbon' }"
    v-bind="rootAttrs"
  >
    <select
      :id="elementId"
      v-model="selectValue"
      v-bind="{ ...selectAttrs, ...ariaBindings }"
      class="nx-native-select"
      :class="`size-${props.size}`"
      :data-save-status="visualState"
      :disabled="isDisabled"
    >
      <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
      <option
        v-for="opt in options"
        :key="String(opt.value)"
        :value="String(opt.value)"
        :disabled="opt.disabled"
      >
        {{ opt.label }}
      </option>
    </select>
    <!-- The kit's own chevron, inset from the border; the ribbon keeps the native one. -->
    <ChevronDownIcon v-if="props.size !== 'ribbon'" filled class="chevron" aria-hidden="true" />
    <NxSaveStateBadge
      class="save-badge"
      :state="visualState"
      :error-message="errorMessage"
      :fade-out="fadeOut"
    />
    <span class="nx-sr-only" aria-live="polite">{{ srText }}</span>
  </span>
</template>

<style scoped>
.nx-native-select {
  font-family: var(--nxt-font);
  font-weight: var(--nxt-font-weight-medium);
  color: var(--nxt-select-fg);
  /* Resting fill + border come from the `input` design axis (data-nx-input);
     hover/focus feedback is the same in all modes. See theme/design/design.css. */
  background: var(--nxt-input-bg);
  border: var(--nxt-hairline) solid var(--nxt-input-border);
  border-radius: var(--nxt-radius-sm);
  outline: none;
  cursor: pointer;
  transition:
    border-color 0.15s,
    background-color 0.15s,
    box-shadow 0.15s;

  /* :not(:focus) so a focused select ignores hover — focus always wins. */
  &:hover:not(:focus):not(:disabled) {
    border-color: var(--nxt-border);
  }

  &:focus {
    border-color: var(--nxt-select-border-focus);
    box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring-soft);
  }

  &:disabled {
    cursor: not-allowed;
    background: var(--nxt-select-bg-disabled);
    color: var(--nxt-input-fg-disabled);
  }
}
/* The native arrow sat right at the border (macOS, I-3178): it is replaced by
   the chevron below, inside the end padding every size but the ribbon keeps. */
.nx-native-select:not(.size-ribbon) {
  appearance: none;
}
.chevron {
  position: absolute;
  inset-block-start: 50%;
  inset-inline-end: var(--nxt-space-sm);
  translate: 0 -50%;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  pointer-events: none;
}
.nx-native-select.size-compact + .chevron {
  inset-inline-end: var(--nxt-space-xs);
  font-size: var(--nxt-font-size-xs);
}
.nx-native-select:disabled + .chevron {
  color: var(--nxt-input-fg-disabled);
}

.nx-native-select.size-compact {
  font-size: var(--nxt-font-size-xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-xl) var(--nxt-space-2xs) var(--nxt-space-xs);
  max-width: calc(var(--nxt-size-unit) * 8.125);
}

/* The default sits next to text inputs in forms: same type size and block
   size as an NxTextInput, so a row of fields lines up. `compact` stays the
   dense variant for toolbars and table rows. */
.nx-native-select.size-field {
  min-height: var(--nxt-control-size-md);
  font-size: var(--nxt-font-size-sm);
  padding: var(--nxt-space-xs) var(--nxt-space-xl) var(--nxt-space-xs) var(--nxt-space-sm);
  max-width: var(--nxt-panel-width-sm);
}

.nx-native-select.size-full {
  font-size: var(--nxt-font-size-sm);
  padding: var(--nxt-space-xs) var(--nxt-space-xl) var(--nxt-space-xs) var(--nxt-space-sm);
  width: 100%;
  max-width: none;
}

/* The ribbon's control (Write's and Calc's font, size and paragraph selects):
   the metrics come from the ribbon (`office-ribbon.css`), with field-sized
   fallbacks outside one. It fills its root, so the consumer's width on the
   component is the select's width. */
.nx-native-select.size-ribbon {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: var(--office-ribbon-control-height, var(--nxt-control-size-sm));
  padding: 0 calc(var(--nxt-size-unit) * 0.375);
  border-color: var(--office-ribbon-border, var(--nxt-border));
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-bg);
  color: var(--office-ribbon-fg, var(--nxt-select-fg));
  font-family: var(--office-ribbon-font-family, var(--nxt-font));
  font-size: var(--office-ribbon-font-size, var(--nxt-font-size-sm));
  font-weight: var(--nxt-font-weight-regular);
  text-overflow: ellipsis;
}

/* --- I-422 save status: corner badge ---
   The root hugs the select (inline-block, no size change) and anchors the badge;
   size-full needs a stretching block root so the select's width: 100% still
   refers to the surrounding layout. */
.nx-native-select-root {
  position: relative;
  display: inline-block;
}
/* Hug the select even when a parent (flex/grid) stretches the root — the
   corner badge anchors to this box, so it must match the field. The ribbon
   size leaves the width to the consumer. */
.nx-native-select-root:not(.root-ribbon) {
  width: fit-content;
}
/* A flex box, not a line box: an inline-block root would add the line's
   descent under the select and make the ribbon row taller than the control. */
.nx-native-select-root.root-ribbon {
  display: inline-flex;
}
.nx-native-select-root.root-full {
  display: block;
  width: 100%;
}
.save-badge {
  position: absolute;
  top: calc(-1 * var(--nxt-space-md) * 0.438);
  right: calc(-1 * var(--nxt-space-md) * 0.438);
}
/* Pinned to the root's start: at its static place after a full-width select it
   stuck out by its own width and gave a scrolling parent a scrollbar (I-3383). */
.nx-sr-only {
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
</style>
